import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import Link from 'next/link'
import { ChevronRight } from 'lucide-react'
import { useGSAP } from '@gsap/react'

import { gsap } from '@/lib/gsap'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/section-heading'
import { AnimatedHeading } from '@/components/generals/animated-heading'

interface Crumb {
    label: string
    href?: string
    /** `table:id:column` when the label is a database value, for inline editing. */
    cms?: string
}

interface PageHeaderProps {
    eyebrow?: string
    title: string
    subtitle?: string
    description?: string
    crumbs?: Crumb[]
    align?: 'left' | 'center'
    children?: ReactNode
}

export const PageHeader: FC<PageHeaderProps> = ({
    eyebrow,
    title,
    subtitle,
    description,
    crumbs,
    align = 'left',
    children
}) => {
    const root = useRef<HTMLElement>(null)
    const t = useT()
    const centered = align === 'center'

    useGSAP(
        () => {
            const el = root.current
            if (!el) return
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            if (reduce) return

            gsap.from(el.querySelectorAll('[data-ph-fade]'), {
                y: 22,
                opacity: 0,
                duration: 0.8,
                stagger: 0.12,
                ease: 'power3.out',
                delay: 0.12
            })
        },
        { scope: root, dependencies: [] }
    )

    return (
        <section
            ref={root}
            className='relative overflow-hidden bg-navy-deep pb-20 pt-36 text-parchment md:pb-28 md:pt-44'
        >
            <div className='pointer-events-none absolute inset-0 bg-gradient-to-b from-navy-deep via-navy/70 to-navy-deep' />
            <div className='pointer-events-none absolute inset-0 bg-stars opacity-[0.07]' />
            <span className='pointer-events-none absolute -end-6 top-6 select-none font-display text-[18rem] leading-none text-parchment/[0.03] md:text-[24rem]'>
                G
            </span>

            <Container className='relative'>
                <div className={cn('flex flex-col', centered ? 'items-center text-center' : 'items-start')}>
                    {crumbs && crumbs.length > 0 && (
                        <nav
                            data-ph-fade
                            aria-label={t.common.breadcrumb}
                            className='mb-7 flex flex-wrap items-center gap-1.5 text-sm text-parchment/55'
                        >
                            {crumbs.map((crumb, i) => (
                                <span key={crumb.label} className='flex items-center gap-1.5'>
                                    {crumb.href ? (
                                        <Link
                                            href={crumb.href}
                                            className='transition-colors hover:text-gold-soft'
                                        >
                                            {crumb.label}
                                        </Link>
                                    ) : (
                                        <span className='text-parchment/80' data-mdlfy-cms={crumb.cms}>{crumb.label}</span>
                                    )}
                                    {i < crumbs.length - 1 && (
                                        <ChevronRight className='size-3.5 text-parchment/35 rtl:rotate-180' />
                                    )}
                                </span>
                            ))}
                        </nav>
                    )}

                    {eyebrow && (
                        <span data-ph-fade>
                            <Eyebrow light>{eyebrow}</Eyebrow>
                        </span>
                    )}

                    <AnimatedHeading
                        as='h1'
                        text={title}
                        delay={0.18}
                        className='mt-5 max-w-4xl text-balance text-4xl font-semibold text-parchment sm:text-5xl md:text-6xl'
                    />

                    {subtitle && (
                        <p
                            data-ph-fade
                            className='mt-4 font-display text-xl font-medium text-gold-soft sm:text-2xl'
                        >
                            {subtitle}
                        </p>
                    )}

                    {description && (
                        <p
                            data-ph-fade
                            className={cn(
                                'mt-6 text-lg leading-relaxed text-parchment/70',
                                centered ? 'max-w-2xl' : 'max-w-3xl'
                            )}
                        >
                            {description}
                        </p>
                    )}

                    {children && (
                        <div data-ph-fade className='mt-8'>
                            {children}
                        </div>
                    )}
                </div>
            </Container>
        </section>
    )
}
